import React, { memo, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { VStack } from '@adc/parallax-component-library';
import { LocalDate } from '@js-joda/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CountryBottomSheetModal, Footer, Form, Header, LogoActionBar, Main, ScrollContainer, SelectorInput, } from 'Components/utility';
import { BirthdayInput } from 'Components/utility/BirthdayInput';
import DatePicker from 'Components/utility/DatePicker';
import { GenderPicker } from 'Components/utility/GenderPicker';
import { Input } from 'Components/utility/Input';
import { REGISTER } from 'Reducers/register/types';
import { validateFrenchZipCode } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import log from 'Utilities/log';
import { store } from 'Utilities/store';
const mapStateToProps = ({ config: { adultAges, countries } }) => {
    return {
        adultAges,
        countries,
    };
};
const connector = connect(mapStateToProps);
const RegisterPersonalInfo = ({ back, validateNameLength, validateIsMinor, validateDOBAndDisplayError, handleClickSignIn, showFrenchZipCodeError, values: currentValues, adultAges, currentStep, countries, }) => {
    const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
    const [isGenderPickerVisible, setIsGenderPickerVisible] = useState(false);
    const [isPlaceOfBirthBottomSheetModalOpen, setIsPlaceOfBirthBottomSheetModalOpen] = useState(false);
    const validationSchema = Yup.object().shape({
        firstName: Yup.string()
            .required(i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.firstName.errors.required'))
            .trim(),
        lastName: Yup.string()
            .required(i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.lastName.errors.required'))
            .trim(),
        dateOfBirth: Yup.string().required(i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.dob.errors.required')),
        ...(currentValues.country === 'FR' && {
            gender: Yup.string().required(),
            birthCountry: Yup.string().required(),
            birthZipCode: Yup.string().when('birthCountry', {
                is: 'FR',
                then: Yup.string().required(i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplaceZipCode.errors.required')),
                otherwise: Yup.string().notRequired(),
            }),
        }),
    });
    const onSubmit = useCallback(async (values) => {
        try {
            const isValidNameLength = validateNameLength(values.firstName, values.lastName);
            if (!isValidNameLength) {
                return;
            }
            const isFrench = values.country === 'FR';
            const isBirthInFrance = values.birthCountry === 'FR';
            if (isFrench && isBirthInFrance) {
                const isZipValid = validateFrenchZipCode(values.birthZipCode);
                if (!isZipValid) {
                    showFrenchZipCodeError();
                    return;
                }
            }
            const register = {
                ...currentValues,
                ...values,
            };
            const isMinor = validateIsMinor(values.dateOfBirth, adultAges[values.country.toUpperCase()]);
            store.dispatch({
                type: REGISTER,
                register: { ...register, isMinor, currentStep: currentStep + 1 },
            });
        }
        catch (err) {
            log.error(err);
        }
    }, [
        validateNameLength,
        currentValues,
        validateIsMinor,
        adultAges,
        currentStep,
        showFrenchZipCodeError,
    ]);
    const { handleSubmit, handleChange, handleBlur, setFieldValue, setFieldTouched, setFieldError, isValid, errors, touched, values, } = useFormik({
        initialValues: {
            ...currentValues,
        },
        validationSchema,
        onSubmit,
        validateOnBlur: true,
    });
    const handleOnBack = useCallback(() => {
        back({ ...values });
    }, [back, values]);
    return (React.createElement(Main, null,
        React.createElement(LogoActionBar, { actionButtonTestID: "RegisterPersonalInfo.action-go-back", titleTestID: "RegisterPersonalInfo.actionTitle", title: i18n.t('CreateAccountPersonalInfo.title'), onBack: handleOnBack }),
        isDatePickerVisible && (React.createElement(VStack, { backgroundColor: "$surface.overlay", position: "absolute", zIndex: 1, style: {
                width: '100vw',
                height: '100vh',
            } })),
        React.createElement(ScrollContainer, null,
            React.createElement(Header, { titleTestID: "RegisterPersonalInfo.title", subtitleTestID: "RegisterPersonalInfo.subtitle", title: i18n.t('CreateAccountPersonalInfo.title'), subtitle: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.primaryText') }),
            React.createElement(Form, null,
                React.createElement(Input, { "aria-label": i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.firstName.label'), placeholder: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.firstName.placeholder'), label: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.firstName.label'), testID: "firstName", inputMode: "text", errorMessage: errors.firstName, isInvalid: errors.firstName && touched.firstName ? true : false, value: values.firstName, onChangeText: handleChange('firstName'), onBlur: handleBlur('firstName'), disabled: isDatePickerVisible }),
                React.createElement(Input, { "aria-label": i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.lastName.label'), placeholder: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.lastName.placeholder'), label: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.lastName.label'), testID: "lastName", inputMode: "text", errorMessage: errors.lastName, isInvalid: errors.lastName && touched.lastName ? true : false, value: values.lastName, onChangeText: handleChange('lastName'), onBlur: handleBlur('lastName'), disabled: isDatePickerVisible }),
                React.createElement(BirthdayInput, { error: errors.dateOfBirth && touched.dateOfBirth ? true : false, errorMessage: errors.dateOfBirth, testID: "dateOfBirth", isTouched: touched.dateOfBirth, initialDate: values.dateOfBirth ? LocalDate.parse(values.dateOfBirth) : undefined, onDateChange: (date) => {
                        const dateString = date.toString();
                        const isValid = validateDOBAndDisplayError(dateString);
                        if (isValid) {
                            setFieldTouched('dateOfBirth', true);
                            setFieldValue('dateOfBirth', dateString);
                        }
                    } }),
                currentValues.country === 'FR' && (React.createElement(React.Fragment, null,
                    React.createElement(SelectorInput, { testID: "gender", label: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.gender.label'), placeholder: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.gender.placeholder'), "aria-label": i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.gender.label'), value: values.gender
                            ? values.gender === 'undetermined'
                                ? i18n.t('Global.microcopy.genderOptions.other')
                                : i18n.t(`Global.microcopy.genderOptions.${values.gender}`)
                            : '', onClick: () => setIsGenderPickerVisible(true) }),
                    React.createElement(SelectorInput, { testID: "birthCountry", label: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplace.label'), placeholder: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplace.placeholder'), "aria-label": i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplace.label'), value: values.birthCountry
                            ? i18n.t(`Global.microcopy.countries.${values.birthCountry.toUpperCase()}`)
                            : '', onClick: () => setIsPlaceOfBirthBottomSheetModalOpen(true) }),
                    values.birthCountry === 'FR' && (React.createElement(Input, { "aria-label": i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplaceZipCode.label'), placeholder: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplaceZipCode.placeholder'), label: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplaceZipCode.label'), testID: "birthZipCode", inputMode: "numeric", errorMessage: errors.birthZipCode, isInvalid: errors.birthZipCode && touched.birthZipCode ? true : false, value: values.birthZipCode, onChangeText: handleChange('birthZipCode'), onBlur: handleBlur('birthZipCode'), disabled: isDatePickerVisible, maxLength: 5 })))))),
        React.createElement(Footer, { buttonText: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.links.next.label'), buttonTestID: "RegisterPersonalInfo.nextButton", buttonLinkTitle: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.primaryText'), buttonLinkTitleTestID: "RegisterPersonalInfo.alreadyHaveAccount", buttonLinkText: i18n.t('CreateAccountPersonalInfo.content.footerSignIn.links.signIn.label'), buttonLinkTestID: "RegisterPersonalInfo.signIn", onButtonSubmit: handleSubmit, onButtonLinkClick: handleClickSignIn, isButtonDisabled: !values.firstName || !values.lastName || !values.dateOfBirth || !isValid }),
        React.createElement(GenderPicker, { isOpen: isGenderPickerVisible, selectedGender: values.gender, onDone: (selectedGender) => {
                setIsGenderPickerVisible(false);
                setFieldValue('gender', selectedGender);
            } }),
        React.createElement(CountryBottomSheetModal, { isOpen: isPlaceOfBirthBottomSheetModalOpen, onCancel: () => {
                setIsPlaceOfBirthBottomSheetModalOpen(false);
            }, onDone: (country) => {
                if (country !== 'FR') {
                    setFieldValue('birthZipCode', '');
                }
                setFieldValue('birthCountry', country);
                setIsPlaceOfBirthBottomSheetModalOpen(false);
            }, selectedCountry: values.birthCountry, title: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.birthplace.label'), countries: countries }),
        React.createElement(DatePicker, { title: i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.dob.label'), isVisible: isDatePickerVisible, initialDate: currentValues.dateOfBirth, onCancel: () => {
                setIsDatePickerVisible(false);
                if (!values.dateOfBirth) {
                    setFieldError('dateOfBirth', i18n.t('CreateAccountPersonalInfo.content.createAccountPersonalInfoForm.formField.dob.errors.required'));
                }
            }, onSave: (date, resetToDate) => {
                setIsDatePickerVisible(false);
                const isValidDate = validateDOBAndDisplayError(date);
                if (isValidDate) {
                    setFieldValue('dateOfBirth', date);
                    return;
                }
                resetToDate(values.dateOfBirth);
            } })));
};
export default connector(memo(RegisterPersonalInfo));
